import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertCircle, RotateCcw, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Champ « Numéro » : propose le prochain numéro par défaut (création) et vérifie en direct l'absence de doublon
export const NumeroDocumentField = ({ value, onChange, typeDocument, periode, excludeId, onValidite, compact = false }) => {
  const [propose, setPropose] = useState('');
  const [etat, setEtat] = useState({ statut: 'idle', detail: '' });
  const derniereProposition = useRef('');

  useEffect(() => {
    if (excludeId) return; // modification : le numéro existant reste la référence
    let actif = true;
    axios.get(`${API_URL}/documents/prochain-numero`, { params: { type_document: typeDocument, periode: periode || undefined } })
      .then((res) => {
        if (!actif) return;
        setPropose(res.data.numero);
        // On remplace la valeur seulement si l'utilisateur n'a pas saisi un numéro personnalisé
        if (!value || value === derniereProposition.current) onChange(res.data.numero);
        derniereProposition.current = res.data.numero;
      })
      .catch(() => {});
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeDocument, periode, excludeId]);

  useEffect(() => {
    const numero = (value || '').trim();
    if (!numero) { setEtat({ statut: 'idle', detail: '' }); onValidite?.(true); return; }
    setEtat({ statut: 'verif', detail: '' });
    const t = setTimeout(() => {
      axios.get(`${API_URL}/documents/numero-disponible`, { params: { numero, exclude_id: excludeId || undefined } })
        .then((res) => {
          const ok = res.data.disponible;
          setEtat({ statut: ok ? 'ok' : 'doublon', detail: ok ? '' : `Déjà utilisé par ${res.data.utilise_par?.type_document || 'un document'} du ${res.data.utilise_par?.date_emission || '?'}` });
          onValidite?.(ok);
        })
        .catch(() => { setEtat({ statut: 'idle', detail: '' }); onValidite?.(true); });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, excludeId]);

  const estPersonnalise = propose && value && value !== propose;
  return (
    <div className="space-y-1" data-testid="numero-document-field">
      <div className="flex items-center justify-between">
        <Label htmlFor="numero_document" className={compact ? 'text-[10px] uppercase text-slate-400' : 'text-xs'}>Numéro</Label>
        {estPersonnalise && (
          <button type="button" onClick={() => onChange(propose)} className="text-[11px] text-indigo-600 hover:underline flex items-center gap-1" data-testid="numero-reset-defaut">
            <RotateCcw className="w-3 h-3" /> Par défaut ({propose})
          </button>
        )}
      </div>
      <div className="relative">
        <Input id="numero_document" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={propose || 'Attribué automatiquement'}
          className={`font-mono pr-8 ${compact ? 'h-8 text-xs bg-white' : ''} ${etat.statut === 'doublon' ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
          aria-invalid={etat.statut === 'doublon'} data-testid="input-numero-document" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2">
          {etat.statut === 'verif' && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          {etat.statut === 'ok' && <CheckCircle2 className="w-4 h-4 text-emerald-500" data-testid="numero-disponible" />}
          {etat.statut === 'doublon' && <AlertCircle className="w-4 h-4 text-red-500" data-testid="numero-doublon" />}
        </span>
      </div>
      {etat.statut === 'doublon' && <p className="text-xs text-red-600" data-testid="numero-doublon-message">Ce numéro existe déjà — {etat.detail}. Choisissez un autre numéro.</p>}
      {etat.statut !== 'doublon' && !excludeId && propose && !estPersonnalise && <p className="text-[11px] text-slate-400">Prochain numéro de la séquence. Vous pouvez le modifier.</p>}
    </div>
  );
};
